// Cell renderers for the jobs table, ported from ionq-cloud-ui's
// sharedColumns.tsx / JobStatusTag.tsx / NameCell.tsx / JobTag.tsx /
// IdText + IonTableCopyIconWithToast. Same textStyles and pixel metrics on
// stock Chakra variants and legacy-ui tokens (no remote-only variants).

import { Box, Center, HStack, Icon, Tag, Text } from '@chakra-ui/react';
import { toaster } from '@ionq/legacy-ui';
import type { IconType } from 'react-icons';
import {
    MdCancel,
    MdCheckCircle,
    MdContentCopy,
    MdError,
    MdErrorOutline,
    MdGrain,
    MdPending,
} from 'react-icons/md';
import { Link as RouterLink } from 'react-router';
import { Tooltip } from '../../components/Tooltip';
import {
    ACU,
    formatCurrency,
    formatJobDate,
    getIdDisplay,
    getTooltipFormattedDate,
    USD,
} from '../../lib/jobs/format';
import { getJobTypeLabel } from '../../lib/jobs/job-type';
import { getFriendlyTargetName } from '../../lib/jobs/targets';
import { MdPreliminary } from './MdPreliminary';

export const DateCell = ({ request }: { request?: number }) => (
    <Text textStyle='body-8' color='fg' lineClamp={1} w='195px'>
        <span>
            <Tooltip content={`${getTooltipFormattedDate(request)} ago`}>
                {formatJobDate(request)}
            </Tooltip>
        </span>
    </Text>
);

const STATUS_ICON_MAP: Record<string, IconType | undefined> = {
    canceled: MdCancel,
    completed: MdCheckCircle,
    failed: MdError,
    ready: MdPreliminary,
    running: MdPending,
    submitted: MdPending,
};

const JobStatusTag = ({ status }: { status: string }) => {
    const isCompleted = status === 'completed';
    const isFailed = status === 'failed';
    const colorPalette = isCompleted ? 'green' : isFailed ? 'red' : 'gray';
    const icon = STATUS_ICON_MAP[status] || MdPending;

    return (
        <Tag.Root
            colorPalette={colorPalette}
            variant='subtle'
            size='md'
            // Same 5px corner as the type tag beside it (stock is 4px).
            borderRadius='5px'
        >
            {/* 20px, not the donor's 24px: the compact 40px rows keep the
                content band at 20px (10px breathing room each side). */}
            <Center h='20px'>
                <Icon as={icon} boxSize='13px' mr='5px' />
                <Text textStyle='body-9' textTransform='lowercase'>
                    {status}
                </Text>
            </Center>
        </Tag.Root>
    );
};

export const StatusCell = ({ status }: { status: string }) => (
    <HStack gap='4px' minW='95px'>
        <JobStatusTag status={status} />
    </HStack>
);

// One 301px line of 15px Ubuntu Mono fits ~40 chars — names past this may
// truncate, so they carry the full name in a tooltip (the donor's 54 was
// tuned to its two-line clamp).
const MAX_NAME_LENGTH = 38;

export const NameCell = ({
    name,
    width,
    errorMitigation,
    to,
}: {
    name?: string | null;
    width?: string;
    errorMitigation?: boolean;
    /** Job-detail route. When set, the name — 'Untitled' included — renders
     * as the row's link (rows themselves are inert). Relative on purpose,
     * like the row click it replaced: the jobs branch also mounts federated
     * under a host prefix (the enterprise embed), where an absolute path
     * would escape the prefix. */
    to?: string;
}) => {
    const label = name ?? 'Untitled';
    const element = (
        <Text
            as='span'
            textStyle='body-table-name'
            color={name ? 'fg' : 'fg.subtle'}
            // Single line, not the donor's two-line clamp: the compact 40px
            // rows hold a 20px content band.
            lineClamp={1}
            wordBreak='break-all'
            w={width ?? '219px'}
            css={
                to
                    ? { '& a:hover': { textDecoration: 'underline' } }
                    : undefined
            }
        >
            {to ? (
                <RouterLink to={to} state={{ isMyJobs: true }}>
                    {label}
                </RouterLink>
            ) : (
                label
            )}
        </Text>
    );

    return name && name.length > MAX_NAME_LENGTH ? (
        <HStack>
            <Tooltip content={name}>
                <span>{element}</span>
            </Tooltip>
            {errorMitigation ? (
                <Tooltip content='Debiased'>
                    <span>
                        <Icon as={MdErrorOutline} />
                    </span>
                </Tooltip>
            ) : null}
        </HStack>
    ) : (
        element
    );
};

// One 160px line of 15px Ubuntu Mono fits ~21 chars — names past this may
// truncate, so they carry the full name in a tooltip.
const MAX_PROJECT_LENGTH = 18;

/** Project name, trimmed to one ellipsized line with the full name in a
 * tooltip — the donor gave projects a Name-sized column and let long ones
 * wrap the row taller. */
export const ProjectCell = ({ name }: { name?: string }) => {
    const element = (
        <Text
            as='span'
            display='block'
            textStyle='body-8'
            color={name ? 'fg' : 'fg.subtle'}
            truncate
            w='160px'
        >
            {name ?? 'Untitled'}
        </Text>
    );

    return name && name.length > MAX_PROJECT_LENGTH ? (
        <Tooltip content={name}>
            <span>{element}</span>
        </Tooltip>
    ) : (
        element
    );
};

export const BackendCell = ({
    target,
    noiseModel,
    noiseModelName,
}: {
    target?: string;
    noiseModel?: string;
    noiseModelName?: string;
}) => (
    // Pinned like Date/Name (a content-sized column would shift as the
    // virtualized rows mount) but never clipped: 135px = 18 chars of 15px
    // Ubuntu Mono, "Forte Enterprise 3" — the donor's 90px clamp cut it.
    <Text textStyle='body-8' color='fg' w='135px' whiteSpace='nowrap'>
        {target && getFriendlyTargetName(target)}
        {noiseModel && noiseModel !== 'ideal' ? (
            <NoiseIcon noiseModelName={noiseModelName} />
        ) : null}
    </Text>
);

// Unmapped models have no friendly name — render the icon bare rather than
// an empty tooltip bubble (the legacy v2 tooltip didn't open on an empty
// label; the v3 machine does).
const NoiseIcon = ({ noiseModelName }: { noiseModelName?: string }) => {
    const icon = (
        <span>
            <Icon
                boxSize='18px'
                as={MdGrain}
                ml='4px'
                color='fg.muted'
                verticalAlign='sub'
            />
        </span>
    );
    return noiseModelName ? (
        <Tooltip content={noiseModelName}>{icon}</Tooltip>
    ) : (
        icon
    );
};

export const IdCell = ({ id }: { id: string }) => (
    <HStack gap={0} minW='105px'>
        <Text textStyle='body-8' as='span'>
            {getIdDisplay(id)}
        </Text>
        <Icon
            data-testid='Copy-Icon'
            as={MdContentCopy}
            ml={1}
            color='fg.muted'
            cursor='pointer'
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(id).then(
                    () => toaster.success({ title: 'Copied to clipboard' }),
                    () =>
                        toaster.error({
                            title: 'Could not copy to clipboard',
                        }),
                );
            }}
        />
    </HStack>
);

export const TypeCell = ({
    type,
    circuits,
}: {
    type?: string;
    circuits?: number;
}) => (
    <Box maxW='118px'>
        {/* Stock subtle Tag; the inline props pin the legacy rendered look
            (panel fill, emphasized hairline, 5px radius, body-9 label). */}
        <Tag.Root
            size='sm'
            variant='subtle'
            borderRadius='5px'
            borderColor='border.emphasized'
            bg='bg.panel'
            minH='20px'
            px='8px'
            color='fg'
            boxShadow='inset 0 0 0px 1px {colors.border.emphasized}'
        >
            <Box textStyle='body-9' color='fg.muted'>
                {getJobTypeLabel(type, circuits)}
            </Box>
        </Tag.Root>
    </Box>
);

/** Pre-run estimate (the donor's formatEstimatedJobCost): a dash, not a
 * "priced at zero" $0.00, when the job has none — free targets never do. */
export const EstimatedCostCell = ({
    estimatedCostAcu,
    estimatedCostUsd,
    hasAccessToPaidBackend,
    unit,
}: {
    estimatedCostAcu?: number | null;
    estimatedCostUsd?: number | null;
    hasAccessToPaidBackend?: boolean | null;
    unit?: string;
}) => {
    const isCredit = estimatedCostAcu != null;
    const hasEstimate = isCredit || estimatedCostUsd != null;
    const formatted = !hasEstimate
        ? '–'
        : hasAccessToPaidBackend
          ? isCredit
              ? ACU.format(estimatedCostAcu)
              : USD.format(estimatedCostUsd ?? 0)
          : formatCurrency(0, unit);
    return (
        <Text textStyle='body-8' color='fg' maxW='120px'>
            {formatted}
        </Text>
    );
};

export const CostCell = ({
    pending,
    costAcu,
    costUsd,
    hasAccessToPaidBackend,
    unit,
}: {
    /** Not billed yet (jobs.list's cost_pending). */
    pending?: boolean;
    costAcu?: number | null;
    costUsd?: number | null;
    hasAccessToPaidBackend?: boolean | null;
    unit?: string;
}) => {
    if (pending) {
        return (
            <Text
                textStyle='body-8'
                color='fg'
                maxW='120px'
                aria-label='Cost not yet available'
            >
                --
            </Text>
        );
    }
    const costCredits = costAcu;
    const isCredit = costCredits != null;
    const isUsd = costUsd != null;
    const hasCost = isCredit || isUsd;
    const formatted =
        hasAccessToPaidBackend && hasCost
            ? isCredit
                ? ACU.format(costCredits)
                : USD.format(costUsd ?? 0)
            : formatCurrency(0, unit);
    return (
        <Text
            textStyle='body-8'
            color='fg'
            maxW='120px'
            {...(isCredit && { 'aria-label': 'Credits' })}
        >
            {formatted}
        </Text>
    );
};
